import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography, DataGrid, Tooltip, Autocomplete, Spacer, Stack, Chip, Box } from '@emburse/embark-core';
import { useEffect, useState } from 'react';
import { $t, useTranslation } from '../../hooks/useTranslation';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { post, parseData } from '../../api/common';
import { formatDate } from '../../util/dateAdapter';
const getLogViewerLogs = () => post('/Services/LogService.asmx/Get24HourLogs').then(parseData);
var LogSeverity;
(function (LogSeverity) {
    LogSeverity[LogSeverity["Unknown"] = 0] = "Unknown";
    LogSeverity[LogSeverity["Info"] = 1] = "Info";
    LogSeverity[LogSeverity["Low"] = 2] = "Low";
    LogSeverity[LogSeverity["Medium"] = 3] = "Medium";
    LogSeverity[LogSeverity["High"] = 4] = "High";
    LogSeverity[LogSeverity["Critical"] = 5] = "Critical";
})(LogSeverity || (LogSeverity = {}));
const logSeverityEntries = (() => Object.entries(LogSeverity).filter((entry) => Number.isNaN(Number(entry[0]))))();
const logSeverityLabels = (() => {
    const map = {};
    logSeverityEntries.forEach((entry) => {
        const [key, value] = entry;
        map[value] = key;
    });
    return map;
})();
const logSeverityValues = (() => Object.keys(logSeverityLabels).map((key) => Number(key)))();
const logSeverityToEmbarkColor = {
    [LogSeverity.Info]: 'info',
    [LogSeverity.Low]: 'secondary',
    [LogSeverity.Medium]: 'secondary',
    [LogSeverity.High]: 'warning',
    [LogSeverity.Critical]: 'error'
};
// looks for a log message like 'successfully imported 10 of 12 settlements for account with id ...'
const isSettlementImportStatusLog = (log) => log.Message.toLowerCase().indexOf('successfully imported') >= 0;
// takes a message like 'successfully imported 10 of 12 settlements' and returns [10,12]
const parseSettlementImportStatusData = (log) => log.Message.replace(/settlements for account.*/, '')
    .split('imported ')[1]
    .split('of')
    .map((val) => Number(val));
const getSettlementImportCounts = (logs) => {
    let imported = 0;
    let total = 0;
    logs.forEach((log) => {
        if (!isSettlementImportStatusLog(log)) {
            return;
        }
        const [succeeded, tried] = parseSettlementImportStatusData(log);
        imported += succeeded;
        total += tried;
    });
    const returnval = [imported, total];
    return returnval;
};
export default function LogViewer() {
    useTranslation();
    const [logs, setLogs] = useState([]);
    const [logsToShow, setLogsToShow] = useState([]);
    const [shardsFilter, setShardsFilter] = useState([0, 1, 2, 3, 4]);
    const [severityFilter, setSeverityFilter] = useState(logSeverityValues);
    const [settlementImportCount, setSettlementImportCount] = useState([0, 0]);
    useEffect(() => {
        getLogViewerLogs().then((res) => {
            setLogs(JSON.parse(res.Data));
        });
    }, []);
    useEffect(() => {
        setLogsToShow(() => logs
            .filter((log) => shardsFilter.includes(log.ShardID))
            .filter((log) => severityFilter.includes(log.Severity)));
    }, [logs, shardsFilter, severityFilter]);
    useEffect(() => {
        const counts = getSettlementImportCounts(logsToShow);
        setSettlementImportCount(counts);
    }, [logsToShow]);
    const shardsChangeHandler = (event, value) => {
        setShardsFilter(value);
    };
    const logSeverityChangeHandler = (event, value) => {
        setSeverityFilter(value);
    };
    return (_jsxs(SingleColumnLayout, { children: [_jsx(Typography, { variant: "h4", children: $t('Logs last 24 hours') }), _jsx(Spacer, { size: 8 }), _jsx(Typography, { variant: "subtitle1", children: `Successful Settlement Imports: ${settlementImportCount[0]}` }), _jsx(Typography, { variant: "subtitle1", children: `Failed Settlement Imports: ${settlementImportCount[1] - settlementImportCount[0]}` }), _jsx(Spacer, { size: 32 }), _jsxs(Stack, { alignItems: "flex-start", direction: "row", spacing: 3, children: [_jsx(Autocomplete, { id: "shards-filter", style: { width: '350px' }, multiple: true, multipleType: "chips", label: $t('Shards'), value: shardsFilter, limitTags: 5, disableCloseOnSelect: true, showCheckbox: true, options: [0, 1, 2, 3, 4], onChange: shardsChangeHandler }), _jsx(Autocomplete, { id: "severity-filter", style: { width: '475px' }, multiple: true, multipleType: "chips", label: $t('Severity'), value: severityFilter, limitTags: 5, disableCloseOnSelect: true, showCheckbox: true, getOptionLabel: (value) => logSeverityLabels[value], options: logSeverityEntries.map((entry) => entry[1]), onChange: logSeverityChangeHandler })] }), _jsx(Spacer, { size: 16 }), _jsx(DataGrid, { autoHeight: true, columns: [
                    {
                        field: 'CompanyName',
                        headerName: $t('Company'),
                        width: 180,
                        renderCell: (cell) => (_jsx(Tooltip, { placement: "top-start", title: cell.row.CompanyName, children: _jsx(Typography, { noWrap: true, children: cell.row.CompanyName }) }))
                    },
                    {
                        field: 'ShardID',
                        headerName: $t('Shard'),
                        width: 56,
                        renderCell: (cell) => (_jsx(Tooltip, { placement: "top-start", title: `Webserver: ${cell.row.WebServer}`, children: _jsx("span", { children: cell.row.ShardID }) }))
                    },
                    {
                        field: 'UserEmail',
                        headerName: $t('UserEmail'),
                        width: 140,
                        renderCell: (cell) => (_jsx(Tooltip, { placement: "top-start", title: cell.row.UserEmail, children: _jsx("span", { children: cell.row.UserEmail }) }))
                    },
                    {
                        field: 'DateEST',
                        headerName: $t('Date'),
                        width: 100,
                        renderCell: (cell) => (_jsx("div", { children: formatDate(cell.row.DateEST) }))
                    },
                    {
                        field: 'Severity',
                        headerName: $t('Severity'),
                        width: 100,
                        renderCell: (cell) => (_jsx(Chip, { size: "mini", label: logSeverityLabels[cell.row.Severity], color: logSeverityToEmbarkColor[cell.row.Severity] }))
                    },
                    {
                        field: 'Page',
                        headerName: $t('Page'),
                        width: 150,
                        renderCell: (cell) => (_jsx(Tooltip, { placement: "top-start", title: cell.row.Page, children: _jsx("span", { children: cell.row.Page }) }))
                    },
                    {
                        field: 'Message',
                        headerName: $t('Message'),
                        flex: 1,
                        renderCell: (cell) => (_jsx(Box, { sx: {
                                overflow: 'hidden'
                            }, children: _jsx(Tooltip, { placement: "top-start", title: cell.row.Message, children: _jsx(Typography, { noWrap: true, children: cell.row.Message }) }) }))
                    },
                    {
                        field: 'Data',
                        headerName: $t('Data'),
                        flex: 2,
                        renderCell: (cell) => (_jsxs(Box, { sx: {
                                overflow: 'hidden'
                            }, children: [' ', _jsx(Tooltip, { placement: "top-start", title: cell.row.Data, children: _jsx(Typography, { noWrap: true, children: cell.row.Data }) })] }))
                    }
                ], rows: logsToShow.map((log) => ({
                    ...log,
                    id: log.PK
                })), pageSizeOptions: [10, 25, 50, 100] })] }));
}
